import DateFnsUtils from '@date-io/date-fns'
import { yupResolver } from '@hookform/resolvers'
import CardContents from '@ifca-root/react-component/src/components/CardList/CardContents'
import MainHeader from '@ifca-root/react-component/src/components/Header/MainHeader'
import { ContentWrapper } from '@ifca-root/react-component/src/components/Layout/ContentWrapper'
import Loading from '@ifca-root/react-component/src/components/Loading/Loading'
import { Checkbox, TextField } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers'
import { ErrorDialog } from 'components/Dialog/ErrorDialog'
import { AccCodeDropdownFullWidth } from 'components/Dropdown/AccCodeDropdown'
import { AccountFooter } from 'components/Footer/AccountFooter'
import {
  useGetAccountPeriodYearPeriodLazyQuery,
  useGetAssignedCompanyQuery,
  useGetCompanyNameQuery,
  useGetCostCentreCodeLazyQuery,
  useGetEntityCoaLazyQuery,
  useGetJournalTypeCodeLazyQuery,
  useGetMasterCoaCodeQuery,
} from 'generated/graphql'
import { CommonYupValidation } from 'helpers/Form/YupValidation'
import { formatDate } from 'helpers/StringNumberFunction/FormatDate'
import React, { Fragment, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useHistory, useLocation, useParams } from 'react-router'
import * as yup from 'yup'
import CheckBoxIcon from '@material-ui/icons/CheckBox'
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank'

interface GLJournalByCostCentreParamsProps {
  CompanyID: string
  StartDate: Date
  EndDate: Date
  Year: Number
  Period: Number
  AccountCode: string[]
  JournalType: string[]
  JournalNo: string
  AccountName: string
  Remark: string
  CostCentreCode: string[]
  ReferenceNo: string[]
  Department: string
}

export interface IMasterCOAProps {
  Name: string
  MasterCOAID: string
  ParentMasterCOAID: string
  IsControl: boolean
  IsLastNode: boolean
  AccountType: string
  Code: string
  Level: number
}

export interface ICostCentreProps {
  CostCentreID: string
  Name: string
  CompanyID: string
  ParentCostCentreID: string
  Level: number
  Code: string
  MasterCode: string
  IsLastNode: boolean
  IsDefault: boolean
}

export interface IJournalTypeProps {
  JournalType: string
  JournalTypeID: string
}

interface CompanyOption {
  Name: string
  CompanyID: string
}

export const GLJournalByCostCentreParamsForm = (props: any) => {
  let history = useHistory()
  const { CompanyID }: any = useParams()
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />
  const checkedIcon = <CheckBoxIcon fontSize="small" />
  const user = JSON.parse(localStorage.getItem('loggedInUser'))
  let location = useLocation()
  const editData = location?.state as any
  const latestCompany = localStorage.getItem('latestCompany')
  const ParamsSchema = yup.object().shape({
    CompanyID: CommonYupValidation.requireField('Company is required'),
  })
  const [masterCOAData, setMasterCOAData] = useState<IMasterCOAProps[]>([])
  const [costCentreData, setCostCentreData] = useState<ICostCentreProps[]>([])
  const [journalTypeData, setJournalTypeData] = useState<IJournalTypeProps[]>(
    []
  )
  const [errMessage, setErrMessage] = useState(null)
  const [errDialog, setErrDialog] = useState(false)

  const {
    handleSubmit,
    register,
    errors,
    control,
    watch,
    setValue,
    getValues,
  } = useForm<GLJournalByCostCentreParamsProps>({
    defaultValues: {},
    mode: 'all',
    resolver: yupResolver(ParamsSchema),
  })

  // ACCOUNTX API CALLS
  const {
    loading: CompanyLoading,
    error: CompanyError,
    data: { getCompany } = { getCompany: [] },
  } = useGetCompanyNameQuery({
    onError: ({ message }) => {
      let error = message?.substring(15)
      setErrMessage(error)
      setErrDialog(true)
    },
    variables: { CompanyID },
    fetchPolicy: 'network-only',
    onCompleted: () => {
      if (latestCompany) {
        loadEntityCOA({
          variables: { CompanyID: latestCompany },
        })

        loadJournalType({
          variables: {
            CompanyID: latestCompany,
          },
        })

        loadAccountPeriod({
          variables: {
            CompanyID: latestCompany,
            orderByAsc: 'FYear',
          },
        })

        loadCostCentre({
          variables: {
            CompanyID: latestCompany,
            IsLastNode: true,
          },
        })
      }
    },
  })

  const {
    loading: CompanyAssignedLoading,
    error: CompanyAssignedError,
    data: { getAssignedCompanyByUser } = { getAssignedCompanyByUser: [] },
  } = useGetAssignedCompanyQuery({
    fetchPolicy: 'network-only',
    onCompleted: () => {
      if (latestCompany) {
        loadEntityCOA({
          variables: { CompanyID: latestCompany },
        })

        loadJournalType({
          variables: {
            CompanyID: latestCompany,
          },
        })

        loadAccountPeriod({
          variables: {
            CompanyID: latestCompany,
            orderByAsc: 'FYear',
          },
        })

        loadCostCentre({
          variables: {
            CompanyID: latestCompany,
            IsLastNode: true,
          },
        })
      }
    },
  })

  const {
    loading: masterCOALoading,
    error: masterCOAError,
    data: { getMasterCOA } = { getMasterCOA: [] },
  } = useGetMasterCoaCodeQuery({
    onError: ({ message }) => {
      let error = message?.substring(15)
      setErrMessage(error)
      setErrDialog(true)
    },
    fetchPolicy: 'network-only',
    variables: { IsLastNode: true },
  })

  const [
    loadEntityCOA,
    {
      loading: EntityCOALoading,
      data: { getEntityCOA } = { getEntityCOA: [] },
    },
  ] = useGetEntityCoaLazyQuery({
    onError: ({ message }) => {
      let error = message?.substring(15)
      setErrMessage(error)
      setErrDialog(true)
    },
    fetchPolicy: 'network-only',
    onCompleted: () => {
      const masterCOAList =
        getEntityCOA?.length > 0
          ? getEntityCOA?.map(coa => coa?.MasterCOA)
          : getMasterCOA?.filter(
              coa => coa?.IsLastNode === true && coa?.IsControl === false
            )
      setMasterCOAData((masterCOAList as any) as IMasterCOAProps[])
    },
  })

  const [
    loadJournalType,
    {
      loading: JournalTypeLoading,
      data: { getJournalType } = { getJournalType: [] },
    },
  ] = useGetJournalTypeCodeLazyQuery({
    onError: ({ message }) => {
      let error = message?.substring(15)
      setErrMessage(error)
      setErrDialog(true)
    },
    fetchPolicy: 'network-only',
    onCompleted: () => {
      setJournalTypeData(getJournalType)
    },
  })

  const [
    loadAccountPeriod,
    {
      loading: accountPeriodLoading,
      data: { getAccountPeriod } = { getAccountPeriod: [] },
    },
  ] = useGetAccountPeriodYearPeriodLazyQuery({
    onError: ({ message }) => {
      let error = message?.substring(15)
      setErrMessage(error)
      setErrDialog(true)
    },
    fetchPolicy: 'network-only',
  })

  const [
    loadCostCentre,
    {
      loading: CostCentreLoading,
      data: { getCostCentre } = { getCostCentre: [] },
    },
  ] = useGetCostCentreCodeLazyQuery({
    onError: ({ message }) => {
      let error = message?.substring(15)
      setErrMessage(error)
      setErrDialog(true)
    },
    fetchPolicy: 'network-only',
    onCompleted: () => {
      setCostCentreData((getCostCentre as any) as ICostCentreProps[])
    },
  })

  const getFinancialAccountPeriod = getAccountPeriod
    ?.filter(
      x => x?.FYear === watch('Year')
      // && x?.MonthEndClose === false
    )
    ?.sort((a, b) => a.FPeriod - b.FPeriod)

  const getObjectValues = (values: string[], dataType: string) => {
    switch (dataType) {
      case 'AccountCode':
        return masterCOAData.filter(x => {
          return values?.some(y => y === x.Code)
        })
      case 'JournalType':
        return getJournalType?.filter(x => {
          return values?.some(y => y === x?.JournalType)
        })
      // case 'CostCentreCode':
      //   return getCostCentre?.filter(x => {
      //     return values?.some(y => y === x?.MasterCode)
      //   })
      default:
        return []
    }
  }

  const [selectedDepartment, setSelectedDepartment] = useState(new Set())

  const handleCheckboxDepartmentChange = (event: any) => {
    let department = selectedDepartment

    if (event?.target?.checked) {
      department.add(event?.target?.value)
    } else {
      department.delete(event?.target?.value)
    }
    setSelectedDepartment(new Set(department))
  }

  const onSubmit = (data, status) => {
    history.push({
      pathname: `/digital-reports/general-ledger/gl-journal-costcentre-report/generated`,
      state: {
        CompanyID: data?.CompanyID,
        StartDate: new Date(data.StartDate),
        EndDate: new Date(data.EndDate),
        Year: data.Year,
        Period: data.Period,
        AccountCode:
          !!watch('AccountCode') && watch('AccountCode')?.length
            ? watch('AccountCode')?.join(',')
            : undefined,
        JournalType:
          !!watch('JournalType') && watch('JournalType')?.length
            ? watch('JournalType')?.join(',')
            : undefined,
        JournalNo: data.JournalNo,
        AccountName: data.AccountName,
        Description: data.Description ? data.Description : undefined,
        // CostCentreCode:
        //   !!watch('CostCentreCode') && watch('CostCentreCode')?.length
        //     ? watch('CostCentreCode')?.join(',')
        //     : undefined,
        CostCentreCode: selectedDepartment
          ? Array.from(selectedDepartment)?.toString()
          : undefined,
        ReferenceNo: data.ReferenceNo,
      },
    })
  }

  return (
    <>
      {CompanyLoading && <Loading />}
      {CostCentreLoading && <Loading />}
      {JournalTypeLoading && <Loading />}
      {accountPeriodLoading && <Loading />}
      {CompanyAssignedLoading && <Loading />}

      <MainHeader
        mainBtn="back"
        onClick={() => history.push(`/digital-reports/general-ledger`)}
        smTitle={'General Ledger'}
        title={user?.accountName}
        routeSegments={[
          { name: 'Main Menu' },
          { name: 'Submenu' },
          { name: 'GL Journal Listing By Department', current: true },
        ]}
        rightRouteSegments={[{ name: 'Parameters', current: true }]}
      />
      <ContentWrapper float>
        <CardContents>
          {!CompanyAssignedLoading && !CompanyLoading && (
            <Controller
              render={({ value, onChange }) => {
                const options: CompanyOption[] =
                  !!user?.superUser === false
                    ? getAssignedCompanyByUser
                    : getCompany
                const defVal = getCompany?.filter(
                  x => x?.CompanyID === latestCompany
                )[0]

                return (
                  <Autocomplete
                    options={options || []}
                    getOptionLabel={(option: CompanyOption) => option.Name}
                    fullWidth
                    onChange={(value, newValue: any) => {
                      setValue('CompanyID', newValue?.CompanyID)

                      loadEntityCOA({
                        variables: { CompanyID: newValue?.CompanyID },
                      })

                      loadJournalType({
                        variables: {
                          CompanyID: newValue?.CompanyID,
                        },
                      })

                      loadAccountPeriod({
                        variables: {
                          CompanyID: newValue?.CompanyID,
                          orderByAsc: 'FYear',
                        },
                      })

                      loadCostCentre({
                        variables: {
                          CompanyID: newValue?.CompanyID,
                          IsLastNode: true,
                        },
                      })
                    }}
                    defaultValue={defVal}
                    renderOption={(props, option) => {
                      return <span>{props?.Name}</span>
                    }}
                    renderInput={(params: any) => {
                      return (
                        <div>
                          <TextField
                            {...params}
                            helperText={errors?.CompanyID?.message}
                            error={errors?.CompanyID ? true : false}
                            label="Company"
                            style={{ width: '100%' }}
                            margin="normal"
                            required
                            defaultValue={defVal}
                          />
                        </div>
                      )
                    }}
                  />
                )
              }}
              label="Company"
              name="CompanyID"
              autoComplete="off"
              control={control}
              multiline={true}
              fullWidth
              margin="normal"
              ref={register}
              helperText={errors?.CompanyID?.message}
              error={errors?.CompanyID ? true : false}
              required
              defaultValue={latestCompany}
            />
          )}
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Controller
              required
              as={KeyboardDatePicker}
              name="StartDate"
              label="Start Date"
              control={control}
              onChange={(date: Date | null) => {
                console.log(date)
              }}
              format="dd/MM/yyyy"
              value={watch('StartDate')}
              margin="normal"
              allowKeyboardControl
              ref={register}
              KeyboardButtonProps={{
                'aria-label': 'change date',
              }}
              defaultValue={editData?.StartDate ?? new Date()}
              helperText={errors?.StartDate?.message}
              error={errors?.StartDate ? true : false}
              showTodayButton
              fullWidth
            />
          </MuiPickersUtilsProvider>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Controller
              required
              as={KeyboardDatePicker}
              name="EndDate"
              label="End Date"
              control={control}
              onChange={(date: Date | null) => {
                console.log(date)
              }}
              format="dd/MM/yyyy"
              value={watch('EndDate')}
              margin="normal"
              allowKeyboardControl
              ref={register}
              KeyboardButtonProps={{
                'aria-label': 'change date',
              }}
              defaultValue={editData?.EndDate ?? new Date()}
              helperText={errors?.EndDate?.message}
              error={errors?.EndDate ? true : false}
              showTodayButton
              fullWidth
            />
          </MuiPickersUtilsProvider>

          {!accountPeriodLoading && (
            <Controller
              render={({ value, onChange }) => {
                return (
                  <Autocomplete
                    options={
                      !!watch(`CompanyID`)
                        ? getAccountPeriod
                            ?.map(el => el?.FYear)
                            .filter((element, index) => {
                              return (
                                getAccountPeriod
                                  ?.map(el => el?.FYear)
                                  .indexOf(element) === index
                              )
                            })
                            .sort((a, b) => b - a)
                        : []
                    }
                    getOptionLabel={option => `${option}`}
                    noOptionsText="Please set up Account Period"
                    fullWidth
                    onChange={(value, newValue: any) => {
                      setValue('Year', newValue)
                    }}
                    renderOption={(props, option) => {
                      return <span>{props}</span>
                    }}
                    renderInput={(params: any) => {
                      return (
                        <div>
                          <TextField
                            {...params}
                            error={errors?.Year ? true : false}
                            label="Financial Year"
                            style={{ width: '100%' }}
                            margin="normal"
                          />
                        </div>
                      )
                    }}
                  />
                )
              }}
              label="Financial Year"
              name="Year"
              autoComplete="off"
              control={control}
              multiline={true}
              fullWidth
              margin="normal"
              ref={register}
              error={errors?.Year ? true : false}
            />
          )}

          {/* <Controller
            render={({ value, onChange }) => {
              return (
                <Autocomplete
                  options={
                    getAccountPeriod
                      ?.filter(x => x?.FYear === Number(watch('Year')))
                      .sort((a, b) => b.FPeriod - a.FPeriod) || []
                  }
                  getOptionLabel={option => `${option?.FPeriod}`}
                  fullWidth
                  onChange={(value, newValue: any) => {
                    setValue('Period', newValue?.FPeriod)
                  }}
                  renderOption={(props, option) => {
                    return <span>{props?.FPeriod}</span>
                  }}
                  renderInput={(params: any) => {
                    return (
                      <div>
                        <TextField
                          {...params}
                          error={errors?.Period ? true : false}
                          label="Financial Period"
                          style={{ width: '100%' }}
                          margin="normal"
                        />
                      </div>
                    )
                  }}
                />
              )
            }}
            label="Financial Period"
            name="Period"
            autoComplete="off"
            control={control}
            multiline={true}
            fullWidth
            margin="normal"
            ref={register}
            error={errors?.Period ? true : false}
          /> */}

          <Controller
            render={({ value, onChange }) => {
              return (
                <Autocomplete
                  options={getFinancialAccountPeriod || []}
                  getOptionLabel={option => String(option?.FPeriod)}
                  noOptionsText="Please set up Account Period"
                  fullWidth
                  value={
                    getFinancialAccountPeriod.find(
                      option => option?.FPeriod === value
                    ) || null
                  }
                  onChange={(value, newValue: any) => {
                    onChange(newValue?.FPeriod)
                  }}
                  renderOption={(props, option) => {
                    return (
                      <span className="">
                        {`${props?.FPeriod} | ${formatDate(
                          props?.StartDate
                        )} - ${formatDate(props?.EndDate)}`}
                      </span>
                    )
                  }}
                  renderInput={(params: any) => {
                    return (
                      <div>
                        <TextField
                          {...params}
                          label="Financial Period"
                          style={{ width: '100%' }}
                          margin="dense"
                          type="number"
                        />
                      </div>
                    )
                  }}
                />
              )
            }}
            label="Financial Period"
            name="Period"
            autoComplete="off"
            control={control}
            multiline={true}
            fullWidth
            margin="normal"
            ref={register}
            required
            error={errors?.Period ? true : false}
          />

          {!EntityCOALoading && !masterCOALoading && (
            <Controller
              name="AccountCode"
              label="Account Code"
              fullWidth
              margin="dense"
              ref={register}
              control={control}
              render={({ onChange, onBlur, value }) => {
                return (
                  <Autocomplete
                    multiple
                    value={
                      getObjectValues(
                        watch('AccountCode'),
                        'AccountCode'
                      ) as IMasterCOAProps[]
                    }
                    fullWidth
                    options={masterCOAData}
                    disableCloseOnSelect
                    getOptionLabel={option =>
                      `${option?.Name} | ${option?.Code}`
                    }
                    PopperComponent={AccCodeDropdownFullWidth}
                    onChange={(value, newValue: any) => {
                      onChange(newValue?.map(x => x?.Code))
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        variant="standard"
                        label="Account Code"
                      />
                    )}
                    renderOption={(option, { selected }) => {
                      return (
                        <>
                          <Checkbox checked={selected} color="primary" />
                          <div>
                            <div>
                              <span className="xsTitle">{option.Name}</span>
                            </div>
                            <div className="desc">{option.Code}</div>
                          </div>
                        </>
                      )
                    }}
                  />
                )
              }}
            />
          )}

          <Controller
            name="JournalType"
            label="Journal Type"
            fullWidth
            margin="dense"
            ref={register}
            control={control}
            render={({ onChange, onBlur, value }) => {
              return (
                <Autocomplete
                  multiple
                  value={
                    getObjectValues(
                      watch('JournalType'),
                      'JournalType'
                    ) as IJournalTypeProps[]
                  }
                  fullWidth
                  options={getJournalType}
                  disableCloseOnSelect
                  getOptionLabel={option => `${option?.JournalType}`}
                  PopperComponent={AccCodeDropdownFullWidth}
                  onChange={(value, newValue: any) => {
                    onChange(newValue?.map(x => x?.JournalType))
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      variant="standard"
                      label="Journal Type"
                    />
                  )}
                  renderOption={(option, { selected }) => {
                    return (
                      <>
                        <Checkbox checked={selected} color="primary" />
                        <div>
                          <div>
                            <span className="xsTitle">
                              {option.JournalType}
                            </span>
                          </div>
                        </div>
                      </>
                    )
                  }}
                />
              )
            }}
          />

          <Controller
            as={TextField}
            id="standard-basic"
            name="JournalNo"
            label="Journal No"
            autoComplete="off"
            control={control}
            fullWidth
            margin="dense"
            ref={register}
            helperText={errors?.JournalNo?.message}
            error={errors?.JournalNo ? true : false}
          />

          <Controller
            as={TextField}
            id="standard-basic"
            name="AccountName"
            label="Account Name"
            autoComplete="off"
            control={control}
            fullWidth
            margin="dense"
            ref={register}
          />

          <Controller
            as={TextField}
            id="standard-basic"
            name="Remark"
            label="Remark"
            autoComplete="off"
            control={control}
            fullWidth
            margin="dense"
            ref={register}
          />
          {/* {'asas'} */}

          {/* 
          <Controller
            name="CostCentreCode"
            label="Department"
            fullWidth
            margin="dense"
            ref={register}
            control={control}
            render={({ onChange, onBlur, value }) => {
              return (
                <Autocomplete
                  multiple
                  value={
                    getObjectValues(
                      watch('CostCentreCode'),
                      'CostCentreCode'
                    ) as ICostCentreProps[]
                  }
                  fullWidth
                  options={getCostCentre}
                  disableCloseOnSelect
                  getOptionLabel={option => `${option?.MasterCode}`}
                  PopperComponent={AccCodeDropdownFullWidth}
                  onChange={(value, newValue: any) => {
                    onChange(newValue?.map(x => x?.MasterCode))
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      variant="standard"
                      label="Department"
                    />
                  )}
                  renderOption={(option, { selected }) => {
                    return (
                      <>
                        <Checkbox checked={selected} color="primary" />
                        <div>
                          <div>
                            <span className="xsTitle">{option.MasterCode}</span>
                          </div>
                        </div>
                      </>
                    )
                  }}
                />
              )
            }}
          />

          */}

          <Controller
            render={({ value, onChange }) => {
              return (
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={getCostCentre || []}
                  getOptionLabel={option => `${option?.MasterCode}`}
                  fullWidth
                  onChange={(value, newValue: any) => {
                    const selected = new Set()

                    newValue?.map(x => {
                      selected.add(x?.CostCentreID)
                    })

                    setSelectedDepartment(new Set(selected))
                  }}
                  renderOption={(option, { selected }) => (
                    <Fragment>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        value={option?.CostCentreID}
                        style={{ marginRight: 8 }}
                        checked={selected}
                        color="primary"
                        onChange={event => {
                          handleCheckboxDepartmentChange(event)
                        }}
                      />
                      {`${option?.Name}`}
                    </Fragment>
                  )}
                  renderInput={(params: any) => {
                    return (
                      <div>
                        <TextField
                          {...params}
                          variant="outlined"
                          style={{ width: '100%' }}
                          label="Department"
                          margin="normal"
                        />
                      </div>
                    )
                  }}
                />
              )
            }}
            name="Department"
            label="Department"
            margin="normal"
            autoComplete="off"
            fullWidth
            multiline={true}
            control={control}
            select
            ref={register}
          />

          <Controller
            as={TextField}
            id="standard-basic"
            name="ReferenceNo"
            label="Reference No"
            autoComplete="off"
            control={control}
            fullWidth
            margin="dense"
            ref={register}
          />
        </CardContents>

        <AccountFooter
          options={[
            {
              name: 'Submit',
              onClick: () => {
                handleSubmit(onSubmit)()
              },
              color: 'primary',
            },
          ]}
        />
      </ContentWrapper>
      <ErrorDialog
        errorDia={errDialog}
        setErrorDia={setErrDialog}
        errorMsg={errMessage}
        errorHeaderMsg={'Error!'}
      />
    </>
  )
}
